import { ISOString } from "./date";

export enum DisplayMode {
  LIGHT = "light",
  DARK = "dark",
}

export enum ResourceName {
  USERS = "users",
  ROLES = "roles",
  PERMISSIONS = "permissions",
  PARTNERS = "partners",
  CLIENTS = "clients",
  VENDORS = "vendors",
  VENDORS_TPAS = "vendors/tpas",
  VENDORS_CARRIERS = "vendors/carriers",
  VENDORS_RX = "vendors/rx",
  POLICIES = "policies",
  STOPLOSS_CLAIMS = "stoploss-claims",
  CLAIM_ITEMS = "claim-items",
  CLAIMANTS = "claimants",
  REIMBURSEMENTS = "reimbursements",
  DOCUMENTS = "documents",
  TASKS = "tasks",
  DOCUMENT_TRANSFORMATIONS = "document-transformations",
  AUDIT = "audit",
  DOCUMENT_LOADS = "document-loads",
  ENDORSEMENTS = "endorsements",
  TASK_TEMPLATES = "task-templates",
  HELP_ARTICLES = "help-articles",
  HELP_CATEGORIES = "help-categories",
  // Suspected duplicate claimant pairs (Story #6717). Its own resource, not a
  // filtered claimants view -- the row is a PAIR.
  DUPLICATE_CLAIMANT_PAIRS = "duplicate-claimant-pairs",
  // #7064: the merge capability. Its own resource because the API gate derives
  // permissions from path segment + method; granted to the pair-triage roles (000232).
  CLAIMANT_MERGES = "claimant-merges",
}

export enum CommonStatus {
  DEFAULT = "default",

  // Success states
  ACTIVE = "active",
  PAID = "paid",
  CLOSED = "closed",
  COMPLETED = "completed",
  REIMBURSED = "reimbursed",
  PARTIALLY_REIMBURSED = "partially_reimbursed",
  SUCCESS = "success",

  // Warning states
  PENDING = "pending",
  PENDED = "pended",
  IN_PROGRESS = "in_progress",
  REVIEW_REQUIRED = "review_required",
  IN_PROCESS = "in_process",

  // Neutral/inactive states
  INACTIVE = "inactive",
  DISABLED = "disabled",
  DRAFT = "draft",
  EXPIRED = "expired",

  // Error states
  DENIED = "denied",
  SUSPENDED = "suspended",
  CANCELLED = "cancelled",
  FAILED = "failed",
  ERROR = "error",
  PROCESSING_FAILED = "processing_failed",
  UPLOAD_FAILED = "upload_failed",
  LOADING_FAILED = "loading_failed",

  // Info states
  OPEN = "open",
  APPROVED = "approved",
  PROCESSING = "processing",
  NEW = "new",
  SUBMITTED = "submitted",
}

export enum AlertType {
  SUCCESS = CommonStatus.SUCCESS,
  ERROR = CommonStatus.ERROR,
  INFO = "info",
  WARNING = "warning",
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export type ValidAny = any;

export interface GenericID {
  id: string;
}

export interface AegleID {
  aegle_id: string;
}

export interface CommonFields extends GenericID, AegleID {}

export interface AuditFields {
  created_by: string;
  created_datetime: ISOString;
  modified_by?: string;
  modified_datetime?: ISOString;
  deleted?: boolean;
  deleted_by?: string;
  deleted_datetime?: ISOString;
}

/**
 * An audit log record from the backend `audit_logs` table.
 * Used by the Event History timeline on endorsement details.
 */
export interface AuditRecord {
  id: string;
  object_id: string;
  object_type: string;
  action: string;
  principal: string;
  principal_name?: string;
  json_data: Record<string, unknown> | null;
  user_id_ref?: string;
  created_by: string;
  created_datetime: ISOString;
  modified_by?: string;
  modified_datetime?: ISOString;
  deleted?: boolean;
  deleted_by?: string;
  deleted_datetime?: ISOString;
}

/**
 * A row-level audit record from the trigger-populated `data_audit` table.
 * Used by AuditLogBox on Show pages.
 */
export interface DataAuditRecord {
  id: number;
  table_name: string;
  record_id: string;
  action: string;
  principal: string;
  json_data: Record<string, unknown> | null;
  created_datetime: ISOString;
}

/**
 * Reference to a user by ID
 */
export interface UserRef {
  user_id_ref: string;
}

/**
 * Reference to a policy by ID
 */
export interface PolicyRef {
  policy_id_ref: string;
}

/**
 * Reference to a partner by ID
 */
export interface PartnerRef {
  partner_id_ref: string;
}

/**
 * Reference to a client by ID
 */
export interface ClientRef {
  client_id_ref: string;
}

/**
 * Reference to a claim item by ID
 */
export interface ClaimRef {
  claim_id_ref: string;
}

/**
 * Reference to a stop-loss claim by ID
 */
export interface StoplossClaimRef {
  stoploss_claim_id_ref: string;
}

/**
 * Reference to a claimant by ID
 */
export interface ClaimantRef {
  claimant_id_ref: string;
}

/**
 * Reference to a role by ID
 */
export interface RoleRef {
  role_id_ref: string;
}

/**
 * Reference to a task template by ID
 */
export interface TemplateRef {
  template_id_ref: string;
}

/**
 * Reference to a reimbursement by ID
 */
export interface ReimbursementRef {
  reimbursement_id_ref: string;
}

/**
 * Reference to an endorsement by ID
 */
export interface EndorsementRef {
  endorsement_id_ref: string;
}

/**
 * Reference to a document by ID
 */
export interface DocumentRef {
  document_id_ref: string;
}

/**
 * Reference to a vendor (or TPA) by ID
 */
export interface VendorRef {
  vendor_id_ref: string;
}

/**
 * Per-vendor run-out role (#5325). vendor_id pairs with a membership entry in
 * tpa_vendor_ids / rx_vendor_ids; runout_cutover_date is an ISO "YYYY-MM-DD" string.
 */
export interface VendorRunout {
  vendor_id: string;
  is_runout: boolean;
  runout_cutover_date?: string;
}

/**
 * References to TPA vendors (many-to-many via junction table)
 */
export interface TpaVendorRefs {
  tpa_vendor_ids?: string[];
  /** Per-vendor run-out role metadata (#5325); optional, additive over the IDs. */
  tpa_vendor_runout?: VendorRunout[];
}

/**
 * References to Rx/PBM vendors (many-to-many via junction table)
 */
export interface RxVendorRefs {
  rx_vendor_ids?: string[];
  /** Per-vendor run-out role metadata (#5325); optional, additive over the IDs. */
  rx_vendor_runout?: VendorRunout[];
}

/**
 * A policy's administering-vendor context, passed into the claims-upload selector
 * (#5591) so it can pin the policy's own TPA/Rx vendors, tag their primary/run-out
 * role (#5325), and pre-fill the primary TPA. Vendor names are resolved against the
 * vendor list the dialog already loads, so only ids + run-out metadata travel here.
 */
export interface PolicyVendorContext {
  tpaVendorIds: string[];
  rxVendorIds: string[];
  tpaRunout?: VendorRunout[];
  rxRunout?: VendorRunout[];
}

/**
 * Reference to a claims item by ID
 */
export interface ClaimsItemRef {
  claims_item_id_ref: string;
}

export interface SourceDocumentsRef {
  source_documents?: string[];
}

export interface TaskRef {
  task_id_ref: string;
}

export type DataSource = "calculated" | "reported";
